/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable no-bitwise */

import isNil from 'lodash/isNil';

/**
 * @import { Content } from '../content/content'
 * @import { Medium } from '../medium/medium'
 * @import { Playlist } from '../playlist/playlist'
 */

// TODO #138 Zavést registry pro detekce strategií?
/**
 * Utilitní třída pro vytvoření strategie přehrávání z obsahu.
 */
export default class ScheduleContent {
  // TODO: Nebo vlastní typ výjimky?
  /**
   * Vytvoří výjimku při více typech (ne)polymorfního obsahu současně .
   * @param {Content} content (Ne)polymorfní obsah.
   * @returns {Error} (Ne)polymorfní obsah má více typů obsahu zároveň.
   */
  static errorMultipleContentTypes(content) {
    return new Error(`Multiple content types for content ${content}.`);
  }

  /**
   * Vrací true pro obsah, který je koncový např. medium nebo prázdný obsah.
   * Externí medium se nepovažuje za koncový obsah.
   * Prázdný obsah se považuje za koncový.
   * @param {Content | undefined} content
   * @returns {boolean}
   */
  static isLeafContent(content) {
    return (
      !content ||
      (content.medium !== undefined &&
        content.medium.type !== 'application/x-kiwi-local-1')
    );
  }

  /**
   * Vrací true pro obsah, který je shodný.
   * TODO: zvážit porovnání všech property objektů.
   * @param {Content | undefined} content1
   * @param {Content | undefined} content2
   * @returns {boolean}
   */
  static isSameContent(content1, content2) {
    const { medium: m1, layout: l1, playlist: p1 } = content1 ?? {};
    const { medium: m2, layout: l2, playlist: p2 } = content2 ?? {};

    if (m1 && m2) {
      return m1.id === m2.id;
    }

    if (p1 && p2) {
      return p1.id === p2.id;
    }

    if (l1 && l2) {
      return l1.id === l2.id;
    }

    return false;
  }

  // TODO #555: Nějak integrovat type guard, ať se typicky ve switch nemusí přetypovávat.
  /**
   * Zjistí právě jeden (i prázdný) typ obsahu z (ne)polymorfního obsahu.
   * @param {Content} content
   * @returns {ContentType} Právě jeden (i prázdný) typ obsahu nebo vyhodí chybu.
   * @throws {Error} (Ne)polymorfní obsah má více typů obsahu zároveň.
   * @throws {TypeError} (Ne)polymorfní obsah není objekt.
   */
  static type(content) {
    const { layout, medium, playlist } = content;
    let t = 0 | 0;
    t += Number(!isNil(layout)) << 0;
    t += Number(!isNil(medium)) << 1;
    t += Number(!isNil(playlist)) << 2;

    // Pokud je t mocnina 2 (včetně 0), tak byl přítomen právě jeden typ obsahu
    // nebo žádný (prázdný obsah).
    // TODO: Zvážit ještě "and trick", aby i výpočet podmínky byl branch-less:
    // https://www.geeksforgeeks.org/program-to-find-whether-a-given-number-is-power-of-2/
    if (t === 0 || (t & (t - 1)) === 0) {
      return /** @type {ContentType} */ (t);
    }

    throw ScheduleContent.errorMultipleContentTypes(content);
  }
}

// TODO #555: Z nějakého důvodu pro číselné hodnoty v enumu nefunguje literal.
/**
 * Číselník typů (ne)polymorfního obsahu. Pro správné fungování
 * {@link ScheduleContent.type} nutné udržovat hodnoty jako mocniny 2.
 * @enum {0 | 1 | 2 | 4}
 * @readonly
 */
export const ContentType = /** @type {const} */ ({
  Empty: /** @type {0} */ (0 | 0),
  Layout: /** @type {1} */ (1 << 0),
  Medium: /** @type {2} */ (1 << 1),
  Playlist: /** @type {4} */ (1 << 2)
});
